import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import { isValidApiToken, readStoredApiToken } from '../utils/apiToken';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => readStoredApiToken());

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [token]);

  const checkAuth = async () => {
    const t = readStoredApiToken();
    if (!t) {
      logout();
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`/api/${encodeURIComponent(t)}/me`);
      setUser(response.data.user);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      // Use /api/auth/login: forwards to EXTERNAL_LOGIN_URL when set (mock IdP users like designer),
      // otherwise same local SQLite flow as legacy /api/login.
      const response = await api.post('/auth/login', { username, password });
      const userData = response.data.user;
      const newToken = userData?.token;
      if (!isValidApiToken(newToken)) {
        return { success: false, error: 'Invalid login response (missing token)' };
      }

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        error: err.response?.data?.error || 'Login failed' 
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post('/api/auth/register', { username, email, password });
      const userData = response.data.user;
      const newToken = userData?.token;
      if (!isValidApiToken(newToken)) {
        return { success: false, error: 'Invalid registration response (missing token)' };
      }

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        error: err.response?.data?.error || 'Registration failed' 
      };
    }
  };

  const logout = async () => {
    const t = localStorage.getItem('token');
    if (t) {
      try {
        await axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${t}` } });
      } catch {
        /* still clear client session */
      }
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

